import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { useApp } from '../context/AppContext';
import NewsletterSignup from './NewsletterSignup';
import { PerfumeSuggestionModal } from './PerfumeSuggestion';

const Footer = () => {
  const { t } = useApp();
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);

  return (
    <footer className="relative z-30 bg-[#0A0A0A] border-t border-[#2C1B18]">
      {/* Newsletter Section */}
      <div className="border-b border-[#2C1B18]">
        <div className="container mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-heading text-xl text-white mb-1">{t('newsletter')}</h3>
              <p className="text-[#A8A29E] text-sm">{t('newsletter_text')}</p>
            </div>
            <div className="w-full md:w-auto md:min-w-[400px]">
              <NewsletterSignup variant="footer" />
            </div>
          </div>
        </div>
      </div>

      {/* Perfume Suggestion Section - Same layout as Newsletter */}
      <div className="border-b border-[#2C1B18]">
        <div className="container mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-heading text-xl text-white mb-1">{t('suggest_perfume')}</h3>
              <p className="text-[#A8A29E] text-sm">{t('suggest_perfume_footer_text')}</p>
            </div>
            <div className="w-full md:w-auto">
              <button
                onClick={() => setShowSuggestionModal(true)}
                className="btn-primary flex items-center justify-center gap-2 px-8 py-3 w-full md:w-auto"
                data-testid="perfume-suggestion-btn"
              >
                <Sparkles size={18} />
                {t('suggest_perfume_btn')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Perfume Suggestion Modal */}
      <PerfumeSuggestionModal 
        isOpen={showSuggestionModal} 
        onClose={() => setShowSuggestionModal(false)} 
      />

      {/* Main Footer */}
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo & Description */}
          <div className="md:col-span-1">
            <Link to="/home" className="font-heading text-2xl block mb-4">
              <span className="zuiver-letters-nav">
                {'Zuiver'.split('').map((letter, index) => (
                  <span key={index} className="zuiver-letter-nav" style={{ animationDelay: `${index * 0.42}s` }}>
                    {letter}
                  </span>
                ))}
              </span>
              <span className="text-white">Parfum</span>
            </Link>
            <p className="text-[#A8A29E] text-sm leading-relaxed">
              {t('footer_description') || 'Luxe geuren tegen onverslaanbare prijzen.'}
            </p>
          </div>

          {/* Klantenservice */}
          <div>
            <h4 className="text-white font-heading text-lg mb-4">{t('customer_service')}</h4>
            <ul className="space-y-2">
              <li>
                <Link to="/faq" className="text-[#A8A29E] hover:text-[#D4AF37] text-sm transition-colors">
                  {t('faq')}
                </Link>
              </li>
              <li>
                <Link to="/verzending" className="text-[#A8A29E] hover:text-[#D4AF37] text-sm transition-colors">
                  {t('shipping_info')}
                </Link>
              </li>
              <li>
                <Link to="/algemene-voorwaarden" className="text-[#A8A29E] hover:text-[#D4AF37] text-sm transition-colors">
                  {t('terms_conditions')}
                </Link>
              </li>
              <li>
                <Link to="/ruilen-retourneren" className="text-[#A8A29E] hover:text-[#D4AF37] text-sm transition-colors">
                  {t('return_policy')}
                </Link>
              </li>
              <li>
                <Link to="/betaalmethodes" className="text-[#A8A29E] hover:text-[#D4AF37] text-sm transition-colors">
                  {t('payment_methods')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Verzending */}
          <div>
            <h4 className="text-white font-heading text-lg mb-4">{t('shipping_info')}</h4>
            <div className="flex flex-wrap gap-3 mb-4">
              {/* DHL Logo */}
              <div className="h-8 px-3 flex items-center justify-center rounded" style={{ backgroundColor: '#FFCC00' }}>
                <span className="text-[#D40511] font-bold text-sm">DHL</span>
              </div>
              {/* PostNL Logo */}
              <div className="h-8 px-3 flex items-center justify-center rounded" style={{ backgroundColor: '#FF6600' }}>
                <span className="text-white font-bold text-sm">PostNL</span>
              </div>
            </div>
            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-3">
              <p className="text-[#D4AF37] text-sm font-bold mb-1">{t('zuiverparfum_direct')}</p>
              <p className="text-[#A8A29E] text-xs">{t('direct_delivery_footer')}</p>
            </div>
          </div>

          {/* Betaalmethodes */}
          <div>
            <h4 className="text-white font-heading text-lg mb-4">{t('payment_methods_title')}</h4>
            <div className="flex flex-wrap gap-3">
              {/* iDEAL Logo */}
              <div className="h-10 w-14 flex items-center justify-center bg-white rounded">
                <svg viewBox="0 0 40 24" className="h-6">
                  <rect fill="#CC0066" width="40" height="24" rx="2"/>
                  <text x="20" y="16" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">iDEAL</text>
                </svg>
              </div>
              {/* Mastercard Logo */}
              <div className="h-10 w-14 flex items-center justify-center bg-white rounded">
                <svg viewBox="0 0 40 24" className="h-6">
                  <circle cx="15" cy="12" r="8" fill="#EB001B"/>
                  <circle cx="25" cy="12" r="8" fill="#F79E1B"/>
                  <path d="M20 6.5a8 8 0 010 11" fill="#FF5F00"/>
                </svg>
              </div>
              {/* Visa Logo */}
              <div className="h-10 w-14 flex items-center justify-center bg-white rounded">
                <svg viewBox="0 0 40 24" className="h-6">
                  <rect fill="#1A1F71" width="40" height="24" rx="2"/>
                  <text x="20" y="16" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold" fontStyle="italic">VISA</text>
                </svg>
              </div>
              {/* Apple Pay Logo */}
              <div className="h-10 w-14 flex items-center justify-center bg-black rounded border border-gray-600">
                <svg viewBox="0 0 40 24" className="h-5">
                  <text x="20" y="16" textAnchor="middle" fill="white" fontSize="8" fontWeight="500"> Pay</text>
                </svg>
              </div>
              {/* Klarna Logo */}
              <div className="h-10 w-14 flex items-center justify-center rounded" style={{ backgroundColor: '#FFB3C7' }}>
                <svg viewBox="0 0 40 24" className="h-6">
                  <text x="20" y="16" textAnchor="middle" fill="#0A0A0A" fontSize="8" fontWeight="bold">Klarna</text>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Footer */}
      <div className="border-t border-[#2C1B18]">
        <div className="container mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#57534E] text-sm">
              © 2025 ZuiverParfum. {t('all_rights_reserved')}.
            </p>
            <div className="flex gap-6">
              <Link to="/algemene-voorwaarden" className="text-[#57534E] hover:text-[#D4AF37] text-sm transition-colors">
                {t('privacy')}
              </Link>
              <Link to="/algemene-voorwaarden" className="text-[#57534E] hover:text-[#D4AF37] text-sm transition-colors">
                {t('terms')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
